import {
  ChainConfig,
  CustomAppointmentOption,
  defaultTherapyDepartments,
  PracticeTherapyDepartment,
} from "@physiofit-digital/physiofit_types";
import { t } from "i18next";
import { StateCreator } from "zustand";
import { resolveReturnButtonText } from "../../features/appointment/utils/returnButtonText";

export type ChainState = {
  chainId: string | undefined;
  currentPracticeId?: string;
  chainConfig: ChainConfig | undefined;

  setChainId: (chainId: string | undefined) => void;
  setCurrentPracticeId: (practiceId: string | undefined) => void;
  setChainConfig: (chainConfig: ChainConfig | undefined) => void;
  resetChainState: () => void;
};

const initialState = {
  chainId: undefined,
  chainConfig: undefined,
};

/**
 * PF-5570: the map key IS the practice id, so an entry missing the field is incomplete, never
 * ambiguous. Normalising once on the way into the store keeps every selector and screen below
 * working on complete entries instead of each of them having to guard.
 */
const normalizeChainConfig = (chainConfig: ChainConfig): ChainConfig => ({
  ...chainConfig,
  practices:
    chainConfig.practices &&
    Object.fromEntries(
      Object.entries(chainConfig.practices).map(([practiceId, practice]) => [
        practiceId,
        {
          ...practice,
          practiceId: practice.practiceId ?? practiceId,
          // Unindexed entries sort last instead of into a NaN comparison.
          practiceIndex: practice.practiceIndex ?? Number.MAX_SAFE_INTEGER,
        },
      ]),
    ),
});

export const createChainSlice: StateCreator<ChainState> = (set) => ({
  ...initialState,

  setChainId: (chainId: string | undefined) => set(() => ({ chainId })),
  setChainConfig: (chainConfig: ChainConfig | undefined) =>
    set(() => ({
      chainConfig: chainConfig && normalizeChainConfig(chainConfig),
    })),
  setCurrentPracticeId: (practiceId: string | undefined) =>
    set(() => ({ currentPracticeId: practiceId })),
  resetChainState: () => set(initialState),
});

export const getChainId = (state: ChainState) => state.chainId;
export const getChainConfig = (state: ChainState) => state.chainConfig;
export const getChainPractices = (state: ChainState) =>
  Object.values(state.chainConfig?.practices ?? {})
    .map((practice) => ({
      ...practice,
      practiceAddress: `${practice.practiceAddress?.street} ${practice.practiceAddress?.houseNumber}, ${practice.practiceAddress?.city} ${practice.practiceAddress?.cityCode}`,
    }))
    .sort((a, b) => a.practiceIndex - b.practiceIndex);
export const getCurrentPracticeId = (state: ChainState) =>
  state.currentPracticeId ?? Object.keys(state.chainConfig?.practices ?? {})[0];

export const getPracticeColorsById = (
  state: ChainState,
  practiceId: string,
) => {
  const practices = state.chainConfig?.practices;
  const practice = state.chainConfig?.practices?.[practiceId];
  return (
    practice?.practiceColors ?? practices?.[0]?.practiceColors ?? undefined
  );
};

export const getPracticeLogoUrlById = (
  state: ChainState,
  practiceId: string,
) => {
  const practice = state.chainConfig?.practices?.[practiceId];
  return practice ? practice.practiceLogoUrl : undefined;
};

export const getCurrentPracticeAppointmentRequestFlags = (
  state: ChainState,
) => {
  const currentPracticeId = state.currentPracticeId;
  if (!currentPracticeId) return undefined;

  const flags =
    state.chainConfig?.practices?.[currentPracticeId]?.appointmentRequestFlags;
  return flags;
};

export const getCurrentPracticePrivacyPolicyPdfUrl = (state: ChainState) => {
  const currentPracticeId = state.currentPracticeId;
  if (!currentPracticeId) return undefined;

  return state.chainConfig?.practices?.[currentPracticeId]
    ?.practicePrivacyPolicyPdfUrl;
};

export const getCurrentPracticeAppointmentTimeslotConfig = (
  state: ChainState,
) => {
  const currentPracticeId = state.currentPracticeId;
  if (!currentPracticeId) return undefined;
  return state.chainConfig?.practices?.[currentPracticeId]
    ?.appointmentTimeslotConfig;
};

export const getConfigPractices = (state: ChainState) =>
  state.chainConfig?.practices;

// PF-5480: disableAppointmentRequests mirrors the reception module server-side (PF-4979), so it — not the
// number of map entries — decides what the patient can pick. Legacy entries exist for non-reception practices.
export const getBookableConfigPractices = (state: ChainState) =>
  Object.values(state.chainConfig?.practices ?? {})
    .filter(
      (practice) =>
        !practice.appointmentRequestFlags?.disableAppointmentRequests,
    )
    .sort((a, b) => a.practiceIndex - b.practiceIndex);

// The whole store is persisted, so a config from a previously visited chain is still around on the next
// visit. Comparing the ids keeps the entry decision from being made on another chain's practices.
export const getChainConfigLoaded = (state: ChainState) =>
  state.chainConfig !== undefined &&
  state.chainConfig.chainId === state.chainId;

// PF-5480: practice name for the "no bookable practice" screen. Only used when the chain has a single
// configured practice — with several, naming one of them would tell the patient the wrong location.
export const getSoleConfigPracticeName = (state: ChainState) => {
  const practices = Object.values(state.chainConfig?.practices ?? {});
  if (practices.length !== 1) return undefined;
  return (
    practices[0].appointmentRequestFlags?.practiceTitle?.trim() ||
    practices[0].practiceName?.trim() ||
    undefined
  );
};

// The appointment-unavailable dead end is the one screen reached before a location is ever selected, so it
// resolves no URL and shows its close-window text instead of a return button.
export const getCurrentPracticeAppointmentReturnUrl = (state: ChainState) => {
  const currentPracticeId = state.currentPracticeId;
  if (!currentPracticeId) return undefined;
  return state.chainConfig?.practices?.[currentPracticeId]?.appointmentReturnUrl;
};

// PF-6095: per location, inheriting the chain-wide label (PF-5795) until the location sets its own. An empty
// label means "use the translated default", so it must not reach the button as an empty label.
export const getCurrentPracticeAppointmentReturnButtonText = (state: ChainState) =>
  resolveReturnButtonText(
    state.currentPracticeId
      ? state.chainConfig?.practices?.[state.currentPracticeId]
          ?.appointmentReturnButtonText
      : undefined,
    state.chainConfig?.appointmentReturnButtonText,
  ) || t("RETURN_TO_WEBSITE");

export const getConversionTrackingConfig = (state: ChainState) =>
  state.chainConfig?.conversionTracking;

const specialChainIds = ["rec8dJh7QFFHN53Nm"]; // Therapium

export const getIsTherapium = (state: ChainState) =>
  specialChainIds.includes(state.chainId ?? "");

const NO_CUSTOM_OPTIONS: CustomAppointmentOption[] = [];

export const getCurrentPracticeCustomAppointmentOptions = (state: ChainState) => {
  const practiceId = state.currentPracticeId;
  return (
    (practiceId
      ? state.chainConfig?.practices?.[practiceId]?.customAppointmentOptions
      : undefined) ?? NO_CUSTOM_OPTIONS
  );
};

export const getBookableTherapyDepartments = (
  state: ChainState,
): PracticeTherapyDepartment[] => {
  const practiceId = state.currentPracticeId;
  const stored = practiceId
    ? state.chainConfig?.practices?.[practiceId]?.therapyDepartments
    : undefined;
  return (stored ?? defaultTherapyDepartments)
    .filter((entry) => entry.enabled)
    .map((entry) => entry.department);
};

